import { EventHeader } from '../EventHeader';
import React from 'react';
import { ChunkedSubCategoriesProps, IState } from '../../contracts/state';
import { observer } from 'mobx-react';
import { Logos } from '@draftkings/event-page-widget-contracts';
import { Marketboard } from '../Marketboard';
import { ParsedCategory, ParsedEvent } from '../../contracts/parsers/IParser';
import { TabsWrapper, Tab, Content, SubTab, SubTabsWrapper } from '@draftkings/dk-tab-switcher';
import { TeamLogoProps } from '@draftkings/scoreboards';
import { TrackEventFunction, LazyRenderMode } from '@draftkings/sportsbook-common-contracts';
import type { LocalizationConfig } from '@draftkings/sportsbook-labels';
import { SGPTurnOnButton } from '../SGPButtons';
import { PlayerPageContextProvider } from '../../contexts/PlayerPageContext/PlayerPageContext';
import { EventPageError } from '../EventPageError';
import { isPopularTab } from '../../helpers';

import '@draftkings/scoreboards/style.css';
import '@draftkings/component-builder/style.css';
import '@draftkings/dk-tab-switcher/style.css';
import './index.scss';

type AppProps = {
    dataProvider: IState;
    logos: Logos;
    trackEvent: TrackEventFunction;
    renderLoader: (showOnlyMarketboard: boolean) => JSX.Element;
    renderContentCardsWidget: () => JSX.Element;
    consumerVersion: string;
    localizationConfig: LocalizationConfig;
    locale: string;
};

type ContentMapArgs = {
    tabId: string;
    controller: IState['tabSwitcherController'];
    subTabs: ParsedCategory[];
    event: ParsedEvent;
    showOverlay: boolean;
    toggleIsExpanded: (subcategoryId: string) => void;
    renderStats: () => JSX.Element;
    renderQuickSgp: () => JSX.Element;
    renderContentCardsWidget: () => JSX.Element;
    trackEvent: TrackEventFunction;
    isError: boolean;
    isLoaded: boolean;
    isLoading: boolean;
    onRefreshButtonClick: (categoryId: string) => void;
    renderLoader: (showOnlyMarketboard: boolean) => JSX.Element;
    chunkedSubCategories: ChunkedSubCategoriesProps[];
    consumerVersion: string;
    getTeamLogo: (props: TeamLogoProps) => JSX.Element;
    lazyRenderMode: LazyRenderMode;
    localizationConfig: LocalizationConfig;
    locale: string;
};

const contentMap: Record<string, (args: ContentMapArgs) => JSX.Element> = {
    Odds: ({
        tabId,
        controller,
        subTabs,
        event,
        showOverlay,
        toggleIsExpanded,
        trackEvent,
        isError,
        isLoaded,
        isLoading,
        onRefreshButtonClick,
        renderLoader,
        chunkedSubCategories,
        consumerVersion,
        getTeamLogo,
        renderContentCardsWidget,
        lazyRenderMode,
        localizationConfig,
        locale
    }) => {
        return (
            <React.Fragment key={tabId}>
                {subTabs.map((subTab) => (
                    <Content key={`${tabId}/${subTab.id}`} route={`${tabId}/${subTab.id}`} controller={controller}>
                        {isError && (
                            <EventPageError
                                title={localizationConfig.eventPage.onLoadEventErrorTitle}
                                text={localizationConfig.eventPage.onLoadEventErrorText}
                                buttonText={localizationConfig.eventPage.refreshButtonText}
                                onClick={() => onRefreshButtonClick(subTab.id)}
                            />
                        )}
                        {event.status !== 'FINISHED' && isLoading && renderLoader(true)}
                        {isLoaded && event.status !== 'FINISHED' && (
                            <>
                                {isPopularTab(subTab.id) && renderContentCardsWidget()}
                                <Marketboard
                                    chunkedSubCategories={chunkedSubCategories}
                                    consumerVersion={consumerVersion}
                                    toggleIsExpanded={toggleIsExpanded}
                                    trackEvent={trackEvent}
                                    getTeamLogo={getTeamLogo}
                                    lazyRenderMode={lazyRenderMode}
                                    localizationConfig={localizationConfig}
                                    locale={locale}
                                >
                                    {showOverlay && (
                                        <div
                                            className="ep-marketboard-overlay"
                                            data-testid="ep-marketboard-overlay"
                                        ></div>
                                    )}
                                </Marketboard>
                            </>
                        )}
                    </Content>
                ))}
            </React.Fragment>
        );
    },
    'Quick Sgp': ({ tabId, controller, renderQuickSgp }) => {
        return (
            <Content key={tabId} route={tabId} controller={controller}>
                {renderQuickSgp()}
            </Content>
        );
    },
    Stats: ({ tabId, controller, renderStats }) => {
        return (
            <Content key={tabId} route={tabId} controller={controller}>
                {renderStats()}
            </Content>
        );
    }
};

class AppComponent extends React.Component<AppProps, IState> {
    constructor(props: AppProps) {
        super(props);
    }

    render() {
        if (this.props.dataProvider.events.length === 0 && this.props.dataProvider.isError) {
            const dict = this.props.localizationConfig.eventPage;
            return (
                <section className="ep-event-page-widget">
                    <div role="alert" className="ep-event-page-widget__error" data-testid="event-page-widget-error">
                        {dict.eventNotAvailable} <a href="/">{dict.homeText}</a>.
                    </div>
                </section>
            );
        }
        return (
            <>
                {this.props.dataProvider.events.length === 0 &&
                    !this.props.dataProvider.isSgpEnabled &&
                    this.props.renderLoader(false)}
                {this.props.dataProvider.events.map((event) => (
                    <section className="ep-event-page-widget" key={event.id}>
                        <EventHeader
                            event={event}
                            leagueName={event.leagueName}
                            logosMap={this.props.logos}
                            isTeamSwap={this.props.dataProvider.isTeamSwap}
                            getTeamLogo={this.props.dataProvider.getTeamLogo}
                            renderStrapline={this.props.dataProvider.renderStraplineWrapper}
                            staticS3Host={this.props.dataProvider.staticS3Host}
                            isSgpEnabled={this.props.dataProvider.isSgpEnabled}
                            isSwooshTeamLogosEnabledForEventPage={
                                this.props.dataProvider.featureFlags['RLS-OSB-WEB-IsSwooshTeamLogosEnabled']?.EventPage
                            }
                            swooshBaseUrl={this.props.dataProvider.productConfig.swooshBaseUrl}
                            locale={this.props.dataProvider.locale}
                            localizationConfig={this.props.localizationConfig}
                        />
                        {this.props.dataProvider.hasSgpToggle && !this.props.dataProvider.isSgpEnabled && (
                            <SGPTurnOnButton
                                infoText={this.props.localizationConfig.eventPage.sgpInfoText}
                                text={this.props.localizationConfig.eventPage.sgpButtonText}
                                ariaLabel={this.props.localizationConfig.eventPage.turnOnSgpModeText}
                                onClick={this.props.dataProvider.setSgpMode}
                            />
                        )}
                        {!this.props.dataProvider.isSgpEnabled && (
                            <PlayerPageContextProvider
                                value={{
                                    getPlayerLink: this.props.dataProvider.getPlayerLink,
                                    onPlayerLinkClick: this.props.dataProvider.onPlayerLinkClick,
                                    playerPageConfig: this.props.dataProvider.playerPageConfig
                                }}
                            >
                                <TabsWrapper dictionary={this.props.localizationConfig.tabSwitcher}>
                                    {this.props.dataProvider.tabs.map((tab) => (
                                        <Tab
                                            route={tab.id}
                                            key={tab.id}
                                            controller={this.props.dataProvider.tabSwitcherController}
                                            isSelectedByDefault={tab.isSelected}
                                            onClick={() => this.props.dataProvider.onTabClick(tab.name)}
                                            navigationUrl={this.props.dataProvider.buildTabNavigationUrl(tab.name)}
                                        >
                                            {tab.displayName}
                                        </Tab>
                                    ))}
                                </TabsWrapper>
                                {this.props.dataProvider.tabs.map((tab) => (
                                    <Content
                                        route={tab.id}
                                        key={tab.id}
                                        controller={this.props.dataProvider.tabSwitcherController}
                                    >
                                        {tab.subTabs.length > 0 && (
                                            <SubTabsWrapper dictionary={this.props.localizationConfig.tabSwitcher}>
                                                {tab.subTabs.map((subTab: ParsedCategory) => (
                                                    <SubTab
                                                        route={`${tab.id}/${subTab.id}`}
                                                        key={subTab.id}
                                                        controller={this.props.dataProvider.tabSwitcherController}
                                                        isSelectedByDefault={tab.isSelected && subTab.isSelected}
                                                        onClick={() =>
                                                            this.props.dataProvider.onSubtabClick(
                                                                subTab.id,
                                                                subTab.name
                                                            )
                                                        }
                                                        navigationUrl={this.props.dataProvider.buildTabNavigationUrl(
                                                            tab.name,
                                                            subTab.name
                                                        )}
                                                    >
                                                        <div>{subTab.name}</div>
                                                    </SubTab>
                                                ))}
                                            </SubTabsWrapper>
                                        )}
                                    </Content>
                                ))}
                                {this.props.dataProvider.tabs.map((tab) => {
                                    return contentMap[tab.name]?.({
                                        tabId: tab.id,
                                        controller: this.props.dataProvider.tabSwitcherController,
                                        showOverlay: this.props.dataProvider.showOverlay,
                                        subTabs: tab.subTabs,
                                        event: event,
                                        toggleIsExpanded: this.props.dataProvider.onToggleExpandClick,
                                        renderQuickSgp: this.props.dataProvider.renderQuickSgpWrapper,
                                        renderStats: this.props.dataProvider.renderStatsWrapper,
                                        trackEvent: this.props.trackEvent,
                                        isError: this.props.dataProvider.isError,
                                        isLoaded: this.props.dataProvider.isLoaded,
                                        isLoading: this.props.dataProvider.isLoading,
                                        onRefreshButtonClick: this.props.dataProvider.onRefreshButtonClick,
                                        renderLoader: this.props.renderLoader,
                                        chunkedSubCategories: this.props.dataProvider.chunkedSubCategories,
                                        consumerVersion: this.props.consumerVersion,
                                        getTeamLogo: this.props.dataProvider.getTeamLogo,
                                        lazyRenderMode: this.props.dataProvider.lazyRenderMode,
                                        renderContentCardsWidget: this.props.renderContentCardsWidget,
                                        localizationConfig: this.props.dataProvider.localizationConfig,
                                        locale: this.props.dataProvider.locale
                                    });
                                })}
                            </PlayerPageContextProvider>
                        )}
                    </section>
                ))}
            </>
        );
    }
}

export const App = observer(AppComponent);
